import React, { useEffect, useState, useMemo } from "react";
import ProfileData from "./ProfileData";
import EmptyState from "../EmptyState/EmptyState";
import ChatTags from "./ChatTags";
import ModalProfileData from "./ModalProfileData";
import { PageHeader, Menu, Dropdown, message, Input, Button } from "antd";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { ImportOutlined, FormOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import CTA from "../../Constants";
import styles from "./ChatInfo.module.scss";
import { useAppContext } from "../../State/Global/AppContext";
import { api } from "../../Requests/Api";
import Recomendations from "../Recomendations/Recomendations";
import "./ChatInfoStyles.scss";
import { useTranslation } from "react-i18next";
import { sortByorder } from "./Helpers";
import { useIsMobileView } from "frontend-lib/Utils/Mobile";
import KnowledgeRecordView from "./KnowledgeRecordView";
import PredictModalMobile from "./PredictModalMobile";
import { Logger } from "frontend-lib/Log";

const { profileWrapper, chatInfoWrapper, emptyStateWrapper, chatInfoWrapperContent, recoWrapper } =
    styles;
const { Search } = Input;

function ChatInfo({
    className,
    active,
    isMobile,
    closeInfoCb,
    isPredictModalVisible,
    handleClosePredictModal,
}) {
    const { t } = useTranslation();
    const { actions, appState } = useAppContext();
    const [recomendations, setRecomendations] = useState([]);
    const [question, setQuestion] = useState("");
    const [customer, setCustomer] = useState({});
    const [searchKnowledge, setSearchKnowledge] = useState("");
    const [recoLoading, setRecoLoading] = useState(false);
    const [customerFields, setCustomerFields] = useState([]);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isCustomerFieldLoading, setIsCustomerFieldLoading] = useState(false);
    const [isUpdateModalLoading, setIsUpdateModalLoading] = useState(false);
    const [convTags, setConvTags] = useState(null);
    const [convTagsGroups, setConvTagsGroups] = useState([]);
    const [agents, setAgents] = useState([]);
    const [isMobileView] = useIsMobileView();
    const [isRecomendationModalVisibile, setIsRecomendationModalVisibile] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState();

    const selectedChatId = useMemo(() => {
        return appState.selectedChat.id;
    }, [appState.selectedChat.id]);

    const [typingTimeout, setTypingTimeout] = useState(null);
    const [modalEdit, setModalEdit] = useState(false);
    const logger = useMemo(() => {
        return new Logger("ChatInfo", api.api_url);
    }, []);

    async function editCustomer(customer) {
        const loaderHash = actions.setLoader();
        try {
            setIsUpdateModalLoading(true);
            await api.customer().update(customer);
            actions.updateCustomerInfo(appState.selectedChat, customer);
            setCustomer(customer);
            setIsProfileModalOpen(false);
            actions.unsetLoader(loaderHash);
            setIsUpdateModalLoading(false);
        } catch (err) {
            setIsUpdateModalLoading(false);
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    }

    async function logout() {
        const loaderHash = actions.setLoader();
        try {
            await api.auth().signOut();
            actions.unsetLoader(loaderHash);
            actions.setAuth(false);
            message.info("Logout");
        } catch (err) {
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    }

    function parseMessages(messages) {
        let text = "";
        if (messages && messages.length > 0) {
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i].userType === CTA.CHATS.USER_TYPE.USER) {
                    text = `${messages[i].text} ${text}`;
                } else {
                    i = 0;
                }
            }
        }
        setQuestion(text);
        setRecomendations([]);
    }

    async function getRecoFromChat() {
        try {
            if (appState.agent?.workspace?.id && question) {
                setRecoLoading(true);
                const res = await api.reco().get(appState.agent.workspace.id, question);
                setRecomendations(res.data);
                setRecoLoading(false);
            }
        } catch (err) {
            console.log(err);
            setRecoLoading(false);
        }
    }

    async function sendNewAnswer(id) {
        const loaderHash = actions.setLoader();
        try {
            setIsRecomendationModalVisibile(false);
            isMobile && handleClosePredictModal();
            await api.chats().sendNewAnswer(id, appState.selectedChat?.userUuid);
            actions.setLikedAnswer(id);
            actions.unsetLoader(loaderHash);
            isMobile && closeInfoCb();
        } catch (err) {
            logger.error(`Error in ChatInfo.js/sendNewAnswer, error is: ${JSON.stringify(err)}`);
        }
    }

    async function onSearchKnowledge(val) {
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        setRecoLoading(true);
        setTypingTimeout(
            setTimeout(async () => {
                setSearchKnowledge(val);
                if (val === "") {
                    getRecoFromChat();
                    setRecoLoading(false);
                    return;
                }
                try {
                    const res = await api.reco().get(appState.agent.workspace.id, val);
                    setRecomendations(res.data);
                    setRecoLoading(false);
                } catch (err) {
                    setRecoLoading(false);
                }
            }, 2000)
        );
    }

    function handleModal(type) {
        setIsProfileModalOpen(true);
        if (type === "edit") {
            setModalEdit(true);
        } else {
            setModalEdit(false);
        }
    }

    useEffect(() => {
        parseMessages(appState.selectedChathistory);
        // eslint-disable-next-line
    }, [appState.selectedChathistory]);

    function getConversationTags() {
        api.tags()
            .getConversationTags(appState.selectedChat.id)
            .then((res) => {
                setConvTags(res.data?.conversationsLabeler);
            })
            .catch((err) => {
                console.log(err);
            });
    }

    function getConversationTagGroups() {
        api.tagsGroups()
            .list()
            .then((res) => {
                setConvTagsGroups(res.data.conversationLabelGroups);
            })
            .catch((err) => {
                console.log(err);
            });
    }

    function editConvTags(tags) {
        setConvTags(tags);
    }

    useEffect(() => {
        setCustomer(appState.selectedChat.customer);
        setAgents(appState.selectedChat.agents);
        setIsCustomerFieldLoading(true);
        api.customer()
            .getCustomerFields()
            .then((res) => {
                setCustomerFields(res.data.customerFields.sort(sortByorder));
                setIsCustomerFieldLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setIsCustomerFieldLoading(false);
            });
        if (appState.selectedChat.id) {
            getConversationTags();
            getConversationTagGroups();
        }
    }, [selectedChatId]);

    const renderInfoWeb = () => {
        if (selectedChatId) {
            return (
                <section
                    className={`${className} ${chatInfoWrapper} ${chatInfoWrapperContent} chat-info`}
                    style={{ zIndex: active ? 1 : 0 }}
                >
                    <PageHeader
                        style={{ paddingLeft: "20px", width: "100%" }}
                        subTitle={
                            <ChatTags
                                convTags={convTags}
                                convTagGroups={convTagsGroups}
                                loadTagsCb={getConversationTags}
                                editConvTagsCb={editConvTags}
                            />
                        }
                        onBack={isMobile && (() => closeInfoCb())}
                        extra={[
                            <Dropdown.Button
                                trigger="click"
                                key={"HeaderMenu"}
                                overlay={
                                    <>
                                        <Menu>
                                            {isMobile && (
                                                <Menu.Item
                                                    key="3"
                                                    onClick={logout}
                                                    icon={<ImportOutlined />}
                                                >
                                                    Logout
                                                </Menu.Item>
                                            )}
                                            <Menu.Item
                                                key="4"
                                                icon={<FormOutlined />}
                                                onClick={() => handleModal("edit")}
                                            >
                                                {t("edit")}
                                            </Menu.Item>
                                            <Menu.Item
                                                key="5"
                                                icon={<ExclamationCircleOutlined />}
                                                onClick={() => handleModal("view")}
                                            >
                                                {t("view")}
                                            </Menu.Item>
                                        </Menu>
                                    </>
                                }
                            />,
                        ]}
                    />
                    <div className={profileWrapper}>
                        <ProfileData
                            agents={agents}
                            customer={customer}
                            customerFields={customerFields}
                            editCustomerCb={editCustomer}
                            isLoading={isCustomerFieldLoading}
                        />
                        <ModalProfileData
                            visible={isProfileModalOpen}
                            onCloseCb={() => setIsProfileModalOpen(false)}
                            isLoading={isUpdateModalLoading}
                            customer={customer}
                            editCustomer={modalEdit}
                            editCustomerCb={editCustomer}
                            customerFields={customerFields}
                        />
                    </div>
                    <div className={recoWrapper}>
                        <Search
                            placeholder={t("filters.search")}
                            allowClear
                            onChange={(evt) => onSearchKnowledge(evt.target.value)}
                            onSearch={onSearchKnowledge}
                            style={{ width: "100%", marginBottom: "10px" }}
                        />
                        <div className="opt-reco__button">
                            <Button disabled={!Boolean(question)} onClick={() => getRecoFromChat()}>
                                {t("filters.showAnswerRecomendation")}
                            </Button>
                        </div>
                        <Recomendations
                            isFullScreen={true}
                            isLoading={recoLoading}
                            searchKnowledge={searchKnowledge}
                            question={question}
                            recomendations={recomendations}
                            addLikeCb={sendNewAnswer}
                        />
                    </div>
                </section>
            );
        } else {
            return (
                <section
                    className={`${className} ${chatInfoWrapper}`}
                    style={{ zIndex: active ? 1 : 0 }}
                >
                    <PageHeader
                        key="chatHandlerHeader"
                        subTitle={t("chatInfo")}
                        onBack={isMobile && (() => closeInfoCb())}
                        extra={
                            isMobile && [
                                <div className="header-actions" key="actions">
                                    <Dropdown.Button
                                        key={"HeaderMenu"}
                                        trigger="click"
                                        overlay={
                                            <Menu>
                                                <Menu.Item
                                                    key="6"
                                                    onClick={logout}
                                                    icon={<ImportOutlined />}
                                                >
                                                    Logout
                                                </Menu.Item>
                                            </Menu>
                                        }
                                    />
                                </div>,
                            ]
                        }
                    />
                    <div className={emptyStateWrapper}>
                        <EmptyState icon={faInfoCircle} text={t("noClient")} />
                    </div>
                </section>
            );
        }
    };

    if (isMobileView) {
        return (
            <>
                <ModalProfileData
                    visible={active}
                    onCloseCb={() => closeInfoCb()}
                    isLoading={isUpdateModalLoading}
                    customer={customer}
                    editCustomer={true}
                    editCustomerCb={editCustomer}
                    customerFields={customerFields}
                    convTags={convTags}
                    convTagGroups={convTagsGroups}
                />
                <PredictModalMobile
                    isVisible={isPredictModalVisible}
                    closePredictModal={handleClosePredictModal}
                    currentText={question}
                    onSearchKnowledge={onSearchKnowledge}
                    recommendations={recomendations}
                    recoLoading={recoLoading}
                    setSelectedAnswer={setSelectedAnswer}
                    setRecommendationModalVisible={() => setIsRecomendationModalVisibile(true)}
                />
                <KnowledgeRecordView
                    isOpen={isRecomendationModalVisibile}
                    onCloseCb={() => setIsRecomendationModalVisibile(false)}
                    answer={selectedAnswer}
                    sendKr={(id) => {
                        sendNewAnswer(id);
                    }}
                />
            </>
        );
    } else return renderInfoWeb();
}
export default ChatInfo;
